import React, { FC, useEffect, useState } from 'react';
import { Title } from '../../../components/developers/page-components/Title';
import { useTranslation } from 'react-i18next';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import {
	fetchFundsReserveConfigActive,
	removeMerchantFundReserveConfig,
} from '../../../store/merchnts/actions/fundsReserveConfig';
import { ButtonComponent } from '../../../components/ButtonComponent';
import { IModalWindow, ModalWindow } from '../../../components/modal/ModalWindow';
import { MerchantsFundReserveModalContent } from '../../../components/merchants/MerchantPage/MerchantsFundReserveModalContent';
import { Spinner } from '../../../components/Spinner';
import { fetchMerchantData } from '../../../store/merchnts/actions/merchant';
import { useParams } from 'react-router-dom';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';

interface IFieldProps {
	key: string,
	title: string,
	value: string
}

export const MerchantFundReservePage: FC = () => {
	const { t } = useTranslation();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const { id: mid } = useParams<{ id: string }>();
	const { merchantState: { merchantData, isMerchantDataLoading, isFundsReserveConfigActiveListDataLoading }, dispatch } = useMerchants();
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});
	const [isApplyModalOpen, setIsApplyModalOpen] = useState<boolean>(false);
	const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<boolean>(false);

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));
	const closeApplyModal = (): void => setIsApplyModalOpen(false);
	const closeRemoveModal = (): void => setIsRemoveModalOpen(false);

	const data = merchantData?.fundsReserveConfigurations?.[0];

	useEffect(() => {
		void fetchFundsReserveConfigActive(dispatch);
	}, []);

	const fundReserveFields = (): IFieldProps[] => {
		const reserveRateType = data?.type === 'PERCENTAGE' ? '%' : 'FIXED';
		const disputeRate = data?.disputeRateMin && data?.disputeRateMax
			? `${data?.disputeRateMin / 100}% - ${data?.disputeRateMax / 100}%`
			: '-';

		return [
			{
				key: 'name',
				title: 'Name',
				value: data?.name ? data?.name : '-',
			},
			{
				key: 'disputeRate',
				title: 'Dispute rate',
				value: disputeRate,
			},
			{
				key: 'reserveRate',
				title: 'Reserve rate',
				value: `${data?.reserveRate / 100} ${reserveRateType}`,
			},
			{
				key: 'period',
				title: 'Reserve hold period',
				value: `${data?.period}d`,
			},
			{
				key: 'reserveRateType',
				title: 'Reserve hold type',
				value: data?.reserveRateType === 'FIXED' ? 'FIXED' : 'ROLLING',
			},
		];
	};

	const getSectionBody = (): JSX.Element => {
		if (isMerchantDataLoading) {
			return (
				<div className="card-body min-height-200">
					<Spinner pageLoader />
				</div>
			);
		}
		if (!merchantData?.fundsReserveConfigurations?.length) {
			return (
				<div className="card-body text-center fs-6 my-5">
					No Funds Reserve Configurations Applied
				</div>
			);
		} else {
			return (
				<div className="card-body d-flex align-items-center justify-content-start justify-content-lg-between flex-wrap">
					{fundReserveFields().map(el => (
						<div className="w-150px mb-5" key={el?.key}>
							<div className="text-muted fs-7">{el?.title}</div>
							<div className="fs-6">{el?.value}</div>
						</div>
					))
					}
				</div>
			);
		}
	};

	const handleSuccess = (): void => {
		closeRemoveModal();
		setModalState({
			isOpen: true,
			modalTitle: t('FUNDS_RESERVE.REMOVE_FUNDS_RESERVE_CONFIG'),
			content: (
				<div className="fs-6">
					Funds reserve configurations was successfully removed!
				</div>
			),
			confirmButtonText: t('PARTNERS.SUCCESS_BTN_NAME'),
			minHeight: 100,
			onConfirm: async () => {
				closeModal();
				await fetchMerchantData(dispatch, mid);
			},
			onClose: closeModal
		});
	};

	const handleError = (): void => {
		closeRemoveModal();
		setModalState({
			isOpen: true,
			modalTitle: t('FUNDS_RESERVE.REMOVE_FUNDS_RESERVE_CONFIG'),
			content: (
				<div className="fs-6 text-danger">
					Failed adding Refund information.
				</div>
			),
			confirmButtonText: t('PARTNERS.SUCCESS_BTN_NAME'),
			minHeight: 100,
			onConfirm: closeModal,
			onClose: closeModal
		});
	};

	return (
		<>
			<div className="card">
				<div className="card-header d-flex align-items-center justify-content-between">
					<Title title={t('MERCHANT.FUNDS_RESERVE')} additionalClass="fs-2" />
					{
						checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && (
							<ButtonComponent
								buttonName={merchantData?.fundsReserveConfigurations?.length ? 'Remove reserve' : 'Apply reserve'}
								additionalClass="btn-sm"
								onButtonClick={(): void => merchantData?.fundsReserveConfigurations?.length
									? setIsRemoveModalOpen(true)
									: setIsApplyModalOpen(true)
								}
								isDisabled={isFundsReserveConfigActiveListDataLoading}
							/>
						)
					}
				</div>
				{getSectionBody()}
			</div>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>

			<ModalWindow
				isOpen={isApplyModalOpen}
				modalTitle={t('MENU.FUNDS_RESERVE_CONFIG')}
				size="lg"
				content={<MerchantsFundReserveModalContent onClose={closeApplyModal} />}
				onClose={closeApplyModal}
			/>

			<ModalWindow
				isOpen={isRemoveModalOpen}
				modalTitle={t('FUNDS_RESERVE.REMOVE_FUNDS_RESERVE_CONFIG')}
				modalText='You are going to remove merchant`s funds reserve configurations. Press "Confirm" to continue.'
				onClose={closeRemoveModal}
				onCancel={closeRemoveModal}
				onConfirm={(): Promise<void> => removeMerchantFundReserveConfig(mid, merchantData?.fundsReserveConfigurations[0]?.id, handleSuccess, handleError)}
				confirmButtonText='Confirm'
			/>
		</>
	);
};
